/**
 * @author Harish.R <harish.r@314ecorp.com>
 * constants for claim status response
 */
import _ from 'lodash';
import dateParser from 'lib/dateParser';

import { CLAIM_STATUS_CODES } from 'app-constants/claims/claimStatusCodes';
import { MONTH_DATE_YEAR_SLASH_FORMAT, SORT_COLUMN_LABELS } from '@veritable/common/constants';
import { PROVIDER_IDENTIFIERS_FORM_FIELDS } from 'app-constants/eligibility';
import systemMapping from 'app-constants/eligibility/fhirSystemMapping.json';

const CLAIM_STATUS_PATIENT_SEARCH_TYPE: EligibilityRequests.IPatientSearchType = 'claim_status';

const CLAIM_AGE_OPTIONS_IN_DAYS = [30, 60, 90, 120];

const CLAIMS_UNAVAILABLE_PAYER_LABEL = 'Payers not available for Claim Status';

const CLAIM_STATUS_FORM_FIELDS: Record<
	keyof ClaimStatusRequestForm.IFormModel,
	{ title: string; placeholder: string; errorMessage: string; info?: string }
> = {
	providerId: {
		title: 'Provider/Organization',
		placeholder: 'Select a Provider/Organization',
		errorMessage: 'Please select a Provider/Organization',
		info: 'Choose a Provider/Organization from the dropdown to make the request.',
	},
	locationId: {
		title: 'Service Location',
		placeholder: 'Select a Service Location',
		errorMessage: 'Please select a Service Location',
	},
	payerId: {
		title: 'Payer',
		placeholder: 'Select a Payer',
		errorMessage: 'Please select a Payer',
		info: 'Please select the payer to which you are submitting the transaction.',
	},
	relationWithSubscriber: {
		title: 'Relationship to the Subscriber',
		placeholder: 'Self',
		errorMessage: 'Please select a Relationship',
	},
	memberIdOfPatient: {
		title: 'Member ID',
		placeholder: 'E.g.: WXI234FJCHI',
		errorMessage: 'Please enter a valid Member ID',
	},
	accountNumberOfPatient: {
		title: 'Account Number',
		placeholder: 'E.g.: WXI234FJCHI',
		errorMessage: 'Please enter a valid Account Number',
	},
	firstNameOfPatient: {
		title: 'First Name',
		placeholder: 'E.g.: John',
		errorMessage: 'Please enter the First Name',
	},
	lastNameOfPatient: {
		title: 'Last Name',
		placeholder: 'E.g.: Doe',
		errorMessage: 'Please enter the Last Name',
	},
	dateOfBirthOfPatient: {
		title: 'Date of Birth',
		placeholder: MONTH_DATE_YEAR_SLASH_FORMAT,
		errorMessage: 'Please enter the Date of Birth',
	},
	genderOfPatient: {
		title: 'Gender',
		placeholder: 'Select a Gender',
		errorMessage: 'Please select a Gender',
	},
	firstNameOfSubscriber: {
		title: 'First Name',
		placeholder: 'E.g.: John',
		errorMessage: 'Please enter the First Name',
	},
	lastNameOfSubscriber: {
		title: 'Last Name',
		placeholder: 'E.g.: Doe',
		errorMessage: 'Please enter the Last Name',
	},
	serviceStartDate: {
		title: 'Service Start Date',
		placeholder: dateParser().startOf('day').format(MONTH_DATE_YEAR_SLASH_FORMAT),
		errorMessage: 'Please enter the service start date',
		info: 'Enter the date on which the service starts.',
	},
	serviceEndDate: {
		title: 'Service End Date',
		placeholder: MONTH_DATE_YEAR_SLASH_FORMAT,
		errorMessage: 'Please enter the service end date',
		info: 'Enter the date on which the service ends. ',
	},
	claimNumber: {
		title: 'Claim Number',
		placeholder: 'E.g.: 2223456E7390',
		errorMessage: 'Please enter the claim number',
		info: 'This is the payer-assigned number for professional, facility, or dental claims. It helps identify the specific claim in claim status results.',
	},
	claimAmount: {
		title: 'Claim Amount (USD)',
		placeholder: 'Enter the Claim Amount',
		errorMessage: 'Please enter the claim amount',
	},
	patientReqFields: {
		title: 'Search Patient by',
		placeholder: 'Select a combination',
		errorMessage: 'Please select a combination',
	},
	providerReqFields: {
		title: 'Search Provider by',
		placeholder: 'Select a combination',
		errorMessage: 'Please select a combination',
	},
	...PROVIDER_IDENTIFIERS_FORM_FIELDS,
};

const REQUIRED_FIELD_MESSAGES: Record<keyof ClaimStatusRequestForm.IFormModel, string> = _.mapValues(
	CLAIM_STATUS_FORM_FIELDS,
	(field) => field.errorMessage,
);

const CLAIM_STATUS_LIST_VIEW_FIELDS = {
	simpleSearch: {
		placeholder: 'Search by Patient Name',
	},
	patientName: {
		label: 'Patient Name',
		placeholder: 'E.g.: John Doe',
	},
	identifier: {
		label: 'Patient ID',
		placeholder: 'Enter Member ID, MRN, SSN, etc.',
		tooltip: 'This is an identifier unique to the patient such as Member ID, SSN, Group Number or Medicaid ID.',
	},
	patientDOB: {
		label: 'Patient DOB',
		placeholder: MONTH_DATE_YEAR_SLASH_FORMAT,
	},
	payer: {
		label: 'Payer',
		placeholder: 'Select a Payer',
		selectAllLabel: 'All Payers',
	},
	dateOfService: {
		label: 'Service Dates',
	},
	claimNumber: {
		label: 'Claim Number',
		placeholder: 'E.g.: 2223456E7390',
	},
	dob: {
		label: 'Date of Birth',
		placeholder: MONTH_DATE_YEAR_SLASH_FORMAT,
	},
	advancedSearchButtons: { reset: 'Clear', submit: 'Search' },

	// filter section
	provider: {
		label: 'Providers/Org',
		placeholder: 'Select the Providers/Organization',
		selectAllLabel: 'All Providers/Organization',
	},
	claimStatus: {
		label: 'Payer Status (277)',
		placeholder: 'Select the Payer Status',
		selectAllLabel: 'All Statuses',
		tooltip: 'Choose the payer statuses by which you wish to view the previous responses.',
	},
	claimAmount: {
		label: 'Billed',
		placeholder: 'E.g.: 500',
	},
	claimAge: {
		label: 'Claim Age (Days)',
		placeholder: 'E.g.: 30',
	},
	claimId: {
		label: 'Claim ID',
		placeholder: 'E.g.: 2223456E7390',
	},
	serviceDate: {
		label: 'Service Date',
		placeholder: MONTH_DATE_YEAR_SLASH_FORMAT,
	},
	sort: {
		label: 'Sort By',
		placeholder: 'Select the option to sort',
	},
	pendingPR: {
		label: 'Pending PR',
		placeholder: 'E.g.: 500',
	},
	inquiryDate: {
		label: 'Inquiry Date',
		placeholder: MONTH_DATE_YEAR_SLASH_FORMAT,
	},
	claimType: {
		label: 'Claim Type',
		placeholder: 'Select the Claim Type',
		selectAllLabel: 'All Claim Types',
	},
	claimFrequency: {
		label: 'Claim Frequency',
		placeholder: 'Select the Claim Frequency',
		selectAllLabel: 'All Claim Frequencies',
	},
	payerResponsibilitySequence: {
		label: 'Payer Responsibility Sequence',
		placeholder: 'Select the Payer Responsibility Sequence',
		selectAllLabel: 'All Payer Responsibility Sequences',
	},
	accountId: {
		label: 'Account ID',
		placeholder: 'E.g.: 123456789',
	},
	guarantor: {
		label: 'Guarantor Name',
		placeholder: 'E.g.: Jane Doe',
	},

	workState: {
		label: 'Claim State',
		placeholder: 'Select the Claim State',
		selectAllLabel: 'All Claim States',
	},

	filterLocation: {
		label: 'Filter by Service Location ',
		placeholder: 'Select the Service Locations',
		selectAllLabel: 'All Service Locations',
	},
	filterButtons: { reset: 'Clear', apply: 'Apply' },
};

const CLAIM_STATUS_TAG_COLORS = _.keyBy(CLAIM_STATUS_CODES, (item) => item.code);

const CLAIM_STATUS_MULTISELECT_OPTIONS: IMultiSelectOption[] = _.map(
	_.sortBy(
		_.filter(CLAIM_STATUS_CODES, (item) => !!item.order),
		(item) => item.order,
	),
	(item) => ({
		value: item.label,
		label: item.label,
		color: item.color,
	}),
);

// columns for the sort table data
const CLAIM_TABLE_SORT_COLUMNS: Record<string, ClaimStatusRequests.ISortColumn> = {
	ClaimAge: 'claimAge',
	ClaimAmount: 'claimAmount',
	PatientResponsibility: 'patientResponsibility',
	PendingPatientResponsibility: 'pendingPatientResponsibility',
};

const CLAIM_TABLE_COLUMN_IDS = {
	claimId: 'title',
	payerStatus: 'status',
	patientName: 'patientName',
	payer: 'payer',
	claimAge: 'claimAge',
	claimFrequency: 'claimFrequency',
	billed: 'claimAmount',
	payerPaid: 'payerPaid',
	patientResponsibility: 'patientResponsibility',
	collectedPR: 'collectedPR',
	pendingPR: 'pendingPR',
	writeOff: 'writeOff',
	worklists: 'worklists',
	serviceDates: 'serviceDates',
	payerResponsibilitySequence: 'payerResponsibilitySequence',
	claimType: 'claimType',
	fhirPatient: 'fhirPatient',
	provider: 'provider',
	actions: 'actions',
	accountNumber: 'accountNumber',
	guarantorName: 'guarantorName',
	updatedBy: 'updatedBy',
	customAttributes: 'customAttributes',
};

// default sort options for the sort table data
const CLAIM_TABLE_DEFAULT_SORT_OPTIONS = [
	{
		apikey: CLAIM_TABLE_SORT_COLUMNS.ClaimAge,
		asc: true,
		label: `Claim Age (${SORT_COLUMN_LABELS.oldToNew})`,
		dependencyColumn: CLAIM_TABLE_COLUMN_IDS.claimAge,
	},
	{
		apikey: CLAIM_TABLE_SORT_COLUMNS.ClaimAmount,
		asc: false,
		label: `Billed Amount (${SORT_COLUMN_LABELS.highToLow})`,
		dependencyColumn: CLAIM_TABLE_COLUMN_IDS.billed,
	},
	{
		apikey: CLAIM_TABLE_SORT_COLUMNS.PatientResponsibility,
		asc: false,
		label: `Patient Responsibility (${SORT_COLUMN_LABELS.highToLow})`,
		dependencyColumn: CLAIM_TABLE_COLUMN_IDS.patientResponsibility,
	},
	{
		apikey: CLAIM_TABLE_SORT_COLUMNS.PendingPatientResponsibility,
		asc: false,
		label: `Pending PR (${SORT_COLUMN_LABELS.highToLow})`,
		dependencyColumn: CLAIM_TABLE_COLUMN_IDS.pendingPR,
	},
];

/**
 * claim status label v/s codes list mapping
 */
const CLAIM_STATUS_CODES_MAPPING = _.mapValues(
	_.groupBy(CLAIM_STATUS_CODES, ({ label }) => label),
	(list) => _.map(list, (item) => item.code),
);

const CLAIM_STATUS_CODES_FOR_AGING = [
	'Pending',
	'Additional Information Required',
	'Acknowledged - Accepted for Processing',
	'Acknowledged - Received',
	'Acknowledged - Forwarded',
	'Acknowledged - Accepted with Warning',
	'Acknowledged - Split',
];

const CLAIM_STATUS_CODES_FOR_POSTING = [
	'Paid',
	'Finalized - Complete, No Further Action',
	'Finalized - Revised',
	'Finalized - Forwarded',
	'Finalized - Not Forwarded',
	'Finalized - No Payment Forthcoming',
];

const CLAIMS_MODAL_CONTENT = 'This payer is available only for Claim Status Checks.';

const CLAIM_STATUS_IDENTIFIER_CODE_MAPPING: ClaimStatusResponse.ClaimResponseIdentifier = {
	claimControlNumber: [
		{
			code: '1K',
			codeSystem: systemMapping['REF-01'],
			valueSystem: systemMapping['REF-02'],
		},
	],
	billingType: [
		{
			code: 'BLT',
			codeSystem: systemMapping['REF-01'],
			valueSystem: systemMapping['REF-02'],
		},
	],
	patientAccountNumber: [
		{
			code: 'EJ',
			codeSystem: systemMapping['REF-01'],
			valueSystem: systemMapping['REF-02'],
		},
	],
	pharmacyPrescriptionNumber: [
		{
			code: 'XZ',
			codeSystem: systemMapping['REF-01'],
			valueSystem: systemMapping['REF-02'],
		},
	],
	voucher: [
		{
			code: 'VV',
			codeSystem: systemMapping['REF-01'],
			valueSystem: systemMapping['REF-02'],
		},
	],
	claimNumber: [
		{
			code: 'D9',
			codeSystem: systemMapping['REF-01'],
			valueSystem: systemMapping['REF-02'],
		},
	],
};

const ACTIVITY_TYPE_CATEGORY_MAPPING: Record<ClaimRequest.IActivityTypeCategory, ClaimRequest.IActivityTypeCategory> = {
	deleted: 'deleted',
	user: 'user',
	system: 'system',
};

const CLAIM_PAYMENT_RECONCILIATION_SOURCE: Record<string, ClaimStatusResponse.IPaymentReconciliation> = {
	x12_835: 'x12_835',
	patient: 'patient',
};

const POST_PAYMENT_STATE: Record<string, ClaimStatusResponse.IPostPaymentState> = {
	autoPosted: 'autoPosted',
	manualPosted: 'manualPosted',
	draftClaim: 'draftClaim',
	multiplePayments: 'multiplePayments',
	eraClaim: 'eraClaim',
	pendingEra: 'pendingEra',
	emptyClaim: 'emptyClaim',
	closedClaim: 'closedClaim',
};

export {
	CLAIM_STATUS_PATIENT_SEARCH_TYPE,
	CLAIM_STATUS_FORM_FIELDS,
	CLAIM_STATUS_LIST_VIEW_FIELDS,
	CLAIM_STATUS_TAG_COLORS,
	CLAIM_STATUS_MULTISELECT_OPTIONS,
	CLAIM_STATUS_CODES_FOR_AGING,
	CLAIM_STATUS_CODES_FOR_POSTING,
	CLAIM_STATUS_CODES_MAPPING,
	CLAIM_AGE_OPTIONS_IN_DAYS,
	CLAIMS_MODAL_CONTENT,
	CLAIMS_UNAVAILABLE_PAYER_LABEL,
	REQUIRED_FIELD_MESSAGES,
	CLAIM_TABLE_SORT_COLUMNS,
	SORT_COLUMN_LABELS,
	CLAIM_STATUS_IDENTIFIER_CODE_MAPPING,
	ACTIVITY_TYPE_CATEGORY_MAPPING,
	CLAIM_PAYMENT_RECONCILIATION_SOURCE,
	CLAIM_TABLE_DEFAULT_SORT_OPTIONS,
	POST_PAYMENT_STATE,
	CLAIM_TABLE_COLUMN_IDS,
};
